import React, { useEffect, useState, useRef } from 'react';
import { Form, Button, Card, Container,Modal } from 'react-bootstrap';
import AxiosInstance from '../AxiosInstance';
import ToastNotification from '../ToastNotification';
import Layout from './Layout';

const EditTable = () => {
  const [tableOptions, setTableOptions] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [tableFields, setTableFields] = useState([]);
  const [formData, setFormData] = useState({});
  const toastRef = useRef(null); 
  const [showForm, setShowForm] = useState(false);
  const [tableData, setTableData] = useState([]);
 const [showEditModal, setShowEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, row: null });

  const idFields = {
  account: "account_id",
  departments: "department_id",
  designations: "designation_id",
  document_types: "id",
  employee_type: "id",
  leave_rejection_reason_lists: "id",
  timesheet_rejection_reasons: "id",
  remarks: "id",
};

  // Fetch config once on mount
 useEffect(() => {
    const fetchConfig = async () => {
        try {
            const res = await AxiosInstance.get('/admin/master-table-config');
            console.log('Response from backend:', res.data);
            const config = res.data;  // <-- here
            if (!config) {
            toastRef.current?.showToast('No config data received from server', 'danger');
            return;
            }
            setTableOptions(Object.keys(config));
        } catch (err) {
            toastRef.current?.showToast('Error fetching table config', 'danger');
            console.error('Fetch config error:', err);
        }
        };
    fetchConfig();
    }, []);

  const apiRoutes = {
  account: "/common/accounts",
  departments: "/common/departments",
  designations: "/common/designations",
  document_types: "/admin/get-document-types",
  leave_rejection_reason_lists: "/common/leaveReasons",
  remarks: "/admin/get-remark-topics",
  timesheet_rejection_reasons: "/common/timesheetReasons",
  employee_type: "/common/employeeType",
};

// Fetch table data when table changes
useEffect(() => {
  if (!selectedTable) return;
  const fetchTableData = async () => {
    try {
      const route = apiRoutes[selectedTable];
      const res = await AxiosInstance.get(route);
      setTableData(res.data);
    } catch (err) {
      console.error("Error loading table data", err);
      toastRef.current?.showToast("Could not fetch table data", "danger");
    }
  };
  fetchTableData();
}, [selectedTable]);

const fetchData = async () => {
  if (!selectedTable) return;
  try {
    const route = apiRoutes[selectedTable];
    const res = await AxiosInstance.get(route);
    setTableData(res.data);
  } catch (err) {
    console.error("Error loading table data", err);
    toastRef.current?.showToast("Could not fetch table data", "danger");
  }
};

 const handleTableChange = async (e) => {
    const table = e.target.value;
    setSelectedTable(table);

    if (!table) {
      setTableFields([]);
      setFormData({});
      return;  // If no table selected, reset fields & form
    }

    try {
      const res = await AxiosInstance.get('/admin/master-table-config');
      const config = res.data;

      if (config && config[table]) {
        setTableFields(config[table]);
      } else {
        setTableFields([]);
        toastRef.current?.showToast('Selected table fields not found', 'warning');
      }
      setFormData({}); // reset form
    } catch (err) {
      toastRef.current?.showToast('Error loading fields', 'danger');
      console.error(err);
    }
  };


  // Handle input change (handle checkbox for boolean)
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Form submit
 const handleSubmit = async (e) => {
  e.preventDefault();

  const field = tableFields[0];
  const newValue = (formData[field] || "").trim().toLowerCase();
  const exists = tableData.some(
    (item) => (item[field] || "").trim().toLowerCase() === newValue
  );

  if (exists) {
    toastRef.current?.showToast("Duplicate value. Not added.", "warning");
    return;
  }

  try {
    await AxiosInstance.post("/admin/master-data/add", {
      table: selectedTable,
      data: formData,
    });

    toastRef.current?.showToast("Data added successfully!", "success");

    setShowEditModal(false); // ✅ CLOSE MODAL on success
    setFormData({});
    await fetchData(); // Optional: refresh table

  } catch (err) {
    toastRef.current?.showToast("Submission failed", "danger");
    console.error("Submit error:", err.response?.data || err);
  }
};

  const columnLabelMap = {
  remark_topic: "Type",
  description: "Description",
  date: "Date",
  file_path: "Attachment",
  is_mandatory: "Mandatory",
  department_name:"Departments",
  account_value:"Accounts",
  designation_name:"Designations",
  name:"Document",
  reason:"Reason",
  type:"Type"
};

// Utility to convert snake_case to Title Case
const toTitleCase = (str) => {
  return str
    .replace(/_/g, " ") // Replace underscores with space
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letters
};

const confirmDelete = async () => {
  const { row } = deleteConfirmation;
  if (!row) return;

  try {
    const idField = idFields[selectedTable];
    const idValue = row[idField];

    await AxiosInstance.delete("/admin/master-data/delete", {
      data: { table: selectedTable, idField, idValue },
    });

    toastRef.current?.showToast("Deleted successfully", "success");
    await fetchData();
  } catch (err) {
    console.error("Delete error", err);
    toastRef.current?.showToast("Delete failed", "danger");
  } finally {
    setDeleteConfirmation({ show: false, row: null });
  }
};

const cancelDelete = () => {
  toastRef.current?.showToast("Delete cancelled", "danger");
  setDeleteConfirmation({ show: false, row: null });
};

const handleUpdate = async () => {
  try {
    const idField = idFields[selectedTable];
    const idValue = selectedRow[idField];

    await AxiosInstance.put("/admin/master-data/update", {
      table: selectedTable,
      idField,
      idValue,
      updatedData: formData,
    });

    toastRef.current?.showToast("Updated successfully", "success");
    setShowEditModal(false);
    setSelectedRow(null);
    await fetchData(); // ✅ reload table
  } catch (error) {
    console.error("Update error", error);
    toastRef.current?.showToast("Update failed", "danger");
  }
};

// ✅ Edit trigger function (open modal)
const handleEdit = (row) => {
  setSelectedRow(row);
  setFormData(row); 
  setIsEditing(true);
  setShowEditModal(true);
};

const handleAdd = () => {
  setFormData({});
  setSelectedRow(null);
  setIsEditing(false); 
  setShowEditModal(true);
};

return (
    <Layout heading="Master Data">
      <Container className="py-4">
        <Card className="p-4 shadow rounded">
          <h3 className="mb-4">Manage Master Data</h3>

          {/* Container for dropdown + button on same row, right-aligned */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            {/* Dropdown - Left end */}
            <Form.Group className="mb-0">
              <Form.Select
                value={selectedTable}
                onChange={handleTableChange}
                style={{ minWidth: "200px" }}
              >
                <option value="">-- Select Category --</option>
                {tableOptions.map((table) => (
                  <option key={table} value={table}>
                    {toTitleCase(table)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Add New Button - Right end */}
            {selectedTable && (
              <Button variant="primary" onClick={handleAdd}>
                <i className="bi bi-plus-lg me-2"></i> Add New
              </Button>
            )}
          </div>

          {selectedTable && (
            <>
              {tableData.length > 0 && (
                <div className="table-responsive mb-4">
                  <table className="table table-hover text-center align-middle table-striped">
                    <thead className="table-primary">
                      <tr>
                        <th>#</th>
                        {Object.keys(tableData[0])
                          .filter(
                            (col) =>
                              col !== "id" &&
                              col !== "is_mandatory" && 
                              !col.endsWith("_id")
                          )
                          .map((col, idx) => (
                            <th key={idx} className="text-start">
                              {columnLabelMap[col] || toTitleCase(col)}
                            </th>
                        ))}
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((row, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                         {Object.entries(row)
                            .filter(
                              ([key]) =>
                                key !== "id" &&
                                key !== "is_mandatory" && 
                                !key.endsWith("_id")
                            )
                            .map(([key, val], i) => (
                              <td
                                key={`${idx}-${i}`}
                                className="text-start text-truncate"
                                style={{ maxWidth: "250px" }}
                              >
                                {val === null || val === undefined || val === "" ? (
                                  <span className="text-muted">N/A</span>
                                ) : (
                                  toTitleCase(String(val))
                                )}
                              </td>
                        ))}
                          <td className="d-flex flex-wrap justify-content-center gap-2">
                            <Button variant="primary" size="sm" onClick={() => handleEdit(row)}>
                              <i
                              className="bi bi-pencil"
                              style={{ cursor: "pointer", fontSize: "1.2rem" }}
                              title="Edit"
                            ></i> 
                            </Button>
                            <Button variant="danger" size="sm"
                              onClick={() => setDeleteConfirmation({ show: true, row })}
                              >
                                <i
                                  className="bi bi-trash"
                                  style={{ cursor: "pointer", fontSize: "1.2rem" }}
                                  
                                  title="Delete"
                                />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

         {showEditModal && (
  <Modal
    show={showEditModal}
    onHide={() => setShowEditModal(false)}
    key={selectedRow?.id || "add"}
  >
    <Modal.Header closeButton>
      <Modal.Title>{isEditing ? "Edit Entry" : "Add New Entry"}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <Form>
        {tableFields.map((field, index) => (
          <Form.Group key={index} className="mb-3">
           <Form.Label>{columnLabelMap[field] || toTitleCase(field)}</Form.Label>
           {field === "is_mandatory" ? (
            <Form.Check
              type="checkbox"
              label="Mandatory Document?"
              checked={formData[field] === 1}
              onChange={(e) => handleChange(field, e.target.checked ? 1 : 0)}
            />
          ) : field === "description" ? (
            <Form.Control
              as="textarea"
              rows={3}
              value={formData[field] || ""}
              onChange={(e) => handleChange(field, e.target.value)}
            />
          ) : (
            <Form.Control
              type="text"
              value={formData[field] || ""}
              onChange={(e) => handleChange(field, e.target.value)}
            />
          )}
          </Form.Group>
        ))}
      </Form>
    </Modal.Body>
    <Modal.Footer>
      <Button
        variant="secondary"
        onClick={() => setShowEditModal(false)}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={isEditing ? handleUpdate : handleSubmit}
      >
        {isEditing ? "Update" : "Save"}
      </Button>
    </Modal.Footer>
  </Modal>
)}

          <Modal show={deleteConfirmation.show} onHide={cancelDelete}>
  <Modal.Header closeButton>
    <Modal.Title>Confirm Deletion</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    Are you sure you want to delete this entry?
  </Modal.Body>
  <Modal.Footer>
    <Button variant="secondary" onClick={cancelDelete}>Cancel</Button>
    <Button variant="danger" onClick={confirmDelete}>Delete</Button>
  </Modal.Footer>
</Modal>

          <ToastNotification ref={toastRef} />
        </Card>
      </Container>
    </Layout>
    
  );
};

export default EditTable;
